import { merge, cloneDeep } from 'lodash';
import { CreativeUnitPackageDto } from '../models/creative-unit-package.dto';
import { CreativeUnitDto, CreativeUnitWithCustomizationsDto } from '../models/creative-unit/creative-unit.dto';
import { CreativeUnitListingDto } from '../models/creative-unit/creative-unit-listing.dto';
import { LayersUtils } from './layers.utils';
import { LayerCreationUtils } from './layer-creation.utils';
import { LayerDto } from '../models/layer/layer.dto';
import { CreativeUnitUtils } from './creative-unit.utils';
import { CustomizationUtils } from './customization.utils';
import { MergePropertiesUtils } from './merge-properties.utils';
import { PublicBusiness } from '../../business/business.entity';

export interface BuildUnitFromListingOptions {
	/**
	 * When set, selects which creative unit id (inside `listing.creativeUnitIds`) the built unit
	 * should be anchored to. When omitted, the first id in `creativeUnitIds` is used.
	 */
	specificCreativeUnitId?: string;

	/**
	 * Optional business context — forwarded to `MergePropertiesUtils.getMergeableProperties`
	 * when processing `listing.mappings`. The API path typically omits this; the Angular
	 * wrapper passes `mergeTagState?.business`.
	 */
	business?: PublicBusiness;

	/**
	 * Frontend-only: the active cart item group. Forwarded to mappings state builders.
	 */
	activeGroup?: any;

	/**
	 * Template-creator flag — skips auto-populating overrides (styles, variables, theme)
	 * so the user can build them up manually.
	 */
	noOverrides?: boolean;
}

/**
 * Pure utility for expanding a `CreativeUnitListingDto` into one or more cart-ready
 * `CreativeUnitWithCustomizationsDto` objects. Ports the behavior of the Angular
 * `AdUnitPackageService.createCreativeUnitFromListing` so both the API's cart path and
 * the frontend's existing flow use the same transformation.
 *
 * Deliberately excluded (handled by callers):
 * - `AdStudioContainerService.applyDynamicDefaultValues` — async, Angular-injected.
 *   The API path achieves parity via `applyCustomizationsToPackage`, which runs
 *   `prepareDynamicOptions` + `applyDefaultValues` before this util is invoked.
 * - Customization cropping cleanup — a frontend UI concern.
 */
export class CreativeListingUtils {
	/**
	 * Build a single cart-ready unit from a listing and a specific creative unit id.
	 *
	 * Never inspects `hidden`. `hidden` ("Hide from Add Dialog") only suppresses a size's own
	 * card in the add dialog — authors hide raw units and expose them through listings, so a
	 * hidden listing or unit is still a valid group member and must build normally. Rejecting
	 * a directly submitted id is transport policy and lives in
	 * `project-cart.controller.ts:resolveSingleId`. Throws here were added by a8332b2, removed
	 * by #9793, then restored by a 2026-08-31 release merge — don't add them back.
	 */
	public static buildUnitFromListing(
		listing: CreativeUnitListingDto,
		pkg: CreativeUnitPackageDto,
		options: BuildUnitFromListingOptions = {}
	): CreativeUnitWithCustomizationsDto {
		if (!listing) {
			throw new Error('CreativeListingUtils.buildUnitFromListing: listing is required');
		}
		if (!pkg) {
			throw new Error('CreativeListingUtils.buildUnitFromListing: pkg is required');
		}

		const { specificCreativeUnitId, business, activeGroup, noOverrides } = options;

		let unit: any = {
			id: new Date().getTime().toString() + Math.random().toString(36).substring(2, 9),
			name: listing.name,
			packageId: pkg?.id,
			listingId: listing.id,
			metadata: listing.metadata,
			dimensions: {
				width: listing.dimensions?.width,
				height: listing.dimensions?.height
			},
			templateCreativeUnit: listing?.name || null,
			layers: [],
			locale: {}
		};

		// If the listing is a group, add a group id.
		if ((listing.creativeUnitIds?.length ?? 0) > 1 || (listing.children?.length ?? 0) > 0) {
			unit.group = {
				id: listing.id,
				name: listing.name,
				instanceId: listing.instanceId
			};
		}

		// Find the creative package unit that matches the size.
		const findId = specificCreativeUnitId || listing.creativeUnitIds?.[0];
		let size: CreativeUnitDto | CreativeUnitListingDto | undefined = pkg.creativeUnits?.find(u => u.id === findId);

		// If we didn't find a creative unit, check if findId corresponds to a listing
		if (!size && findId) {
			const foundListing = pkg.creativeUnitListingSettings?.listings?.find(l => l.id === findId);
			if (foundListing && (foundListing.creativeUnitIds?.length ?? 0) > 0) {
				const fallbackCreativeUnitId = foundListing.creativeUnitIds![0];
				size = pkg.creativeUnits?.find(u => u.id === fallbackCreativeUnitId);
			}
		}

		// Final fallback to the original listing if still no size found.
		if (!size) {
			size = listing;
		}

		// Merge the size locale with the listing locale. Listing first, size overrides.
		if (size.locale) {
			unit.locale = {
				...listing.locale,
				...size.locale
			};
		} else if (listing.locale) {
			// If the size has no locale, adopt the listing's directly (matches the
			// observed Angular behavior — unit.locale starts empty and only gets
			// populated if size.locale existed).
			unit.locale = { ...listing.locale };
		}

		// Fill in dimensions if they didn't exist on the listing. Common for group listings.
		if (!unit.dimensions?.width || !unit.dimensions?.height) {
			if (size?.dimensions) {
				unit.dimensions = { ...size.dimensions };
			} else {
				unit.dimensions = {
					width: 300,
					height: 250
				};
			}
		}

		// If the listing is a group, combine the names. Include the locale if it exists.
		if ((listing.creativeUnitIds?.length ?? 0) > 1) {
			unit.name = `${size.name}`;

			if (typeof listing.locale?.name === 'object') {
				const newLocale: any = {
					...unit.locale,
					name: {}
				};

				for (const [key] of Object.entries(listing.locale.name)) {
					newLocale.name[key] = `${(size.locale?.name as any)?.[key] || size.name}`;
				}

				unit.locale = newLocale;
			}
		}

		// If we're creating from the package, find a matching unit and copy its properties.
		let unitFromPackage: CreativeUnitDto | undefined;

		if ('packageId' in size && 'layers' in size) {
			// size is a CreativeUnitDto
			for (const creativeUnit of pkg.creativeUnits || []) {
				if (creativeUnit.id === size.id) {
					unitFromPackage = creativeUnit;
					break;
				}
			}
		} else {
			const sizeAsListing = size as CreativeUnitListingDto;
			if ((sizeAsListing.creativeUnitIds?.length ?? 0) > 0) {
				for (const creativeUnit of pkg.creativeUnits || []) {
					if (creativeUnit.id === sizeAsListing.creativeUnitIds![0]) {
						unitFromPackage = creativeUnit;
						break;
					}
				}
			}
		}

		if (!unitFromPackage) {
			if (unit.dimensions?.width && unit.dimensions?.height) {
				unitFromPackage = CreativeUnitUtils.getMostSimilarCreativeUnit(
					pkg.creativeUnits || [],
					String(unit.dimensions.width),
					String(unit.dimensions.height)
				);
			}
		}

		const overrides: any = {
			...unit,
			dimensions: {
				...unit.dimensions,
				aspectMin: unitFromPackage?.dimensions?.aspectMin,
				aspectMax: unitFromPackage?.dimensions?.aspectMax,
				ppi: unitFromPackage?.dimensions?.ppi,
				defaultDisplayUnit: unitFromPackage?.dimensions?.defaultDisplayUnit || 'px',
				canTransformInto: unitFromPackage?.dimensions?.canTransformInto
			},
			containerClasses: CreativeUnitUtils.getContainerClasses(
				unitFromPackage?.containerClasses,
				pkg.containerClasses,
				unitFromPackage?.appendContainerClasses
			),
			variableOverrides: unitFromPackage?.variableOverrides,
			exportTypes: listing?.exportTypes || unitFromPackage?.exportTypes,
			exportCropBox: unitFromPackage?.exportCropBox,
			scripts: unitFromPackage?.scripts || []
		};

		// Only stamp creativeUnitId on the runtime/cart path, not when we're dealing with the authored originals within the editor
		if (!noOverrides) {
			overrides.creativeUnitId = unitFromPackage?.id;
		}

		// Animation config: package < unit < listing.
		if (pkg?.animationConfig || unitFromPackage?.animationConfig || listing.animationConfig) {
			const packageConfig: any = cloneDeep(pkg?.animationConfig || {});
			const unitConfig: any = cloneDeep(unitFromPackage?.animationConfig || {});
			const listingConfig: any = cloneDeep(listing.animationConfig || {});

			overrides.animationConfig = merge(packageConfig, unitConfig, listingConfig);

			if (packageConfig.gsapPlugins || unitConfig.gsapPlugins || listingConfig.gsapPlugins) {
				const pluginMap = new Map<string, any>();
				if (packageConfig.gsapPlugins) {
					packageConfig.gsapPlugins.forEach((plugin: any) => pluginMap.set(plugin.name, plugin));
				}
				if (unitConfig.gsapPlugins) {
					unitConfig.gsapPlugins.forEach((plugin: any) => pluginMap.set(plugin.name, plugin));
				}
				if (listingConfig.gsapPlugins) {
					listingConfig.gsapPlugins.forEach((plugin: any) => pluginMap.set(plugin.name, plugin));
				}
				overrides.animationConfig.gsapPlugins = Array.from(pluginMap.values());
			}
		}

		if (pkg?.printConfig || unitFromPackage?.printConfig) {
			overrides.printConfig = merge(cloneDeep(pkg?.printConfig || {}), cloneDeep(unitFromPackage?.printConfig || {}));
		}

		if (pkg?.htmlConfig || unitFromPackage?.htmlConfig) {
			overrides.htmlConfig = merge(cloneDeep(pkg?.htmlConfig || {}), cloneDeep(unitFromPackage?.htmlConfig || {}));
		}

		if (pkg?.interactivity || unitFromPackage?.interactivity) {
			overrides.interactivity = merge(cloneDeep(pkg?.interactivity || {}), cloneDeep(unitFromPackage?.interactivity || {}));
		}

		if (listing?.metadata || unitFromPackage?.metadata) {
			overrides.metadata = merge(cloneDeep(listing?.metadata || {}), cloneDeep(unitFromPackage?.metadata || {}));

			// Expose the listing's (group) metadata under a `Group` key so ASM templates can
			// reference group-level metadata via merge tags such as
			// {{activeCreativeUnit.metadata.Group.customVariable}} without colliding with the
			// unit's own flat metadata keys.
			if (listing?.metadata) {
				overrides.metadata.Group = cloneDeep(listing.metadata);
			}
		}

		// Merge Linked Layers with unit layers via the established two-tier merge.
		let layers: LayerDto[] = CreativeUnitUtils.mergePackageLayersWithUnitLayers(pkg?.layers || [], unitFromPackage?.layers || []);

		let createdUnit: CreativeUnitWithCustomizationsDto;
		if (noOverrides) {
			// Template-creator path: the new unit is an authored original seeded from the most similar unit.
			// Linked layers keep only that unit's geometry and its own visibility and layout-group overrides;
			// content and package-level defaults stay on the package layer. `group` comes from the source
			// override, not the merged tree: lodash merges layout arrays by index, the save path replaces them.
			// Unit-specific layers have no package layer to inherit from, so they are copied whole with a new
			// id and owned by the new unit; un-owned they are deleted by sanitizeCreativeUnits on save,
			// together with any linked layers nested inside them (#10635, #10038).
			const sourceLayersById = new Map(
				LayersUtils.findAllLayers(unitFromPackage?.layers || [], () => true).map(layer => [layer.id, layer])
			);
			layers = LayerCreationUtils.reownUnitLayers(cloneDeep(layers), pkg?.layers || [], unit.id, layer => {
				const sourceLayer = sourceLayersById.get(layer.id);
				return {
					id: layer.id,
					type: layer.type,
					label: layer.label,
					style: layer.style,
					hidden: sourceLayer?.hidden,
					group: sourceLayer?.group,
					layers: layer.layers
				} as LayerDto;
			});

			createdUnit = CreativeUnitUtils.createCreativeUnit(overrides, layers, []);
		} else {
			createdUnit = CreativeUnitUtils.createCreativeUnit(overrides, layers, [], unitFromPackage?.styles, pkg?.variables, pkg?.theme);
		}

		// Base customizations carry over from the package / unit.
		let customizations = (unitFromPackage as any)?.customizations || pkg?.customizations || [];

		if ((listing.mappings?.customizations?.length ?? 0) > 0) {
			customizations = CustomizationUtils.mapAllCustomizations(customizations, customization => {
				const mapped = listing.mappings!.customizations!.find(c => c.id === customization.id);
				if (mapped) {
					return {
						...customization,
						dirty: mapped.dirty,
						value: (mapped as any).value
					};
				}
				return customization;
			});
		}

		if (listing.mappings) {
			const state = MergePropertiesUtils.getMergeableProperties(pkg, business, createdUnit, activeGroup);
			createdUnit = CreativeUnitUtils.applyMappingsToCreativeUnit({ ...createdUnit, customizations }, listing.mappings, state, pkg);

			// Unit-specific mapping (the size within a group). Applied after the general
			// mapping so its variable overrides take precedence over the general ones.
			const creativeUnitMapping = listing.mappings.creativeUnits?.find(u => u.id === findId);
			if (creativeUnitMapping) {
				createdUnit = CreativeUnitUtils.applyMappingsToCreativeUnit(
					{ ...createdUnit, customizations },
					creativeUnitMapping,
					state,
					pkg
				);
			}
		} else {
			createdUnit.customizations = customizations;
		}

		return createdUnit;
	}

	/**
	 * Expand a listing into one or more cart-ready units. Handles group fan-out via
	 * `listing.creativeUnitIds` and `listing.children`.
	 *
	 * Every declared member expands, hidden or not — including hidden children, so a design
	 * set is not made unaddable by one hidden child. See the note on `buildUnitFromListing`.
	 *
	 * For a single-unit listing (no `creativeUnitIds`, no `children`), returns one unit
	 * with the listing as its own size — matching the frontend's silent-degrade behavior.
	 */
	public static expandListing(
		listing: CreativeUnitListingDto,
		pkg: CreativeUnitPackageDto,
		options: Omit<BuildUnitFromListingOptions, 'specificCreativeUnitId'> = {}
	): CreativeUnitWithCustomizationsDto[] {
		if (!listing) {
			throw new Error('CreativeListingUtils.expandListing: listing is required');
		}

		// Prefer explicit creativeUnitIds. Groups produce N units.
		if ((listing.creativeUnitIds?.length ?? 0) > 0) {
			return listing.creativeUnitIds!.map(creativeUnitId =>
				this.buildUnitFromListing(listing, pkg, { ...options, specificCreativeUnitId: creativeUnitId })
			);
		}

		// Then children (template-creator design-sets).
		if ((listing.children?.length ?? 0) > 0) {
			return listing.children!.flatMap(child => this.expandListing(child, pkg, options));
		}

		// Single-unit listing with no creativeUnitIds and no children — silent degrade.
		return [this.buildUnitFromListing(listing, pkg, options)];
	}
}
